/**
 * Client binding for the Birdwatch realtime relay (realtime.birdwatch.com) —
 * a pure event relay (SSE over Redis Streams). A publisher (e.g. media-v3 when
 * a transcript finishes) emits once on a channel; every subscribed client is
 * pushed the event live over one SSE connection.
 *
 * The event schema is imported from @birdwatch/common (`realtime-events.ts`) —
 * the one home shared with the relay and media-v3, so the wire contract cannot
 * drift. `createRelayHooks<T>()` is type-only in the browser (no runtime
 * validation); the zod leaves exist so `EventPath`/`EventData` can derive event
 * names + payload types.
 */

import type { realtimeEventSchema } from "@birdwatch/common";
import { createRelayHooks } from "@birdwatch/relay/react";

export type RealtimeEvents = typeof realtimeEventSchema;

/**
 * Subscribe to relay events. Returns `{ status }`; pass `channels` (the room ids —
 * entity ids such as a media `attachmentId` or an `orderId`), the `events` to listen
 * for, and an `onData` handler. Do NOT destructure `onData`'s payload — read
 * `payload.event` / `payload.data` so the discriminated union narrows.
 */
export const { useRelay: useRealtimeRelay } =
  createRelayHooks<RealtimeEvents>();
